import { create } from 'zustand';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useT } from '@/store/i18n';

type Tone = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: string;
  tone: Tone;
  title: string;
  description?: string;
  duration?: number; // ms; 0 = sticky
  action?: { label: string; onClick: () => void };
}

interface ToastState {
  items: ToastItem[];
  show: (input: Omit<ToastItem, 'id'>) => string;
  dismiss: (id: string) => void;
}

const useToastStore = create<ToastState>()((set, get) => ({
  items: [],
  show(input) {
    const id = Math.random().toString(36).slice(2, 10);
    set((s) => ({ items: [...s.items, { ...input, id }] }));
    if (input.duration !== 0) {
      setTimeout(() => get().dismiss(id), input.duration ?? 3500);
    }
    return id;
  },
  dismiss(id) {
    set((s) => ({ items: s.items.filter((t) => t.id !== id) }));
  },
}));

// Convenience helpers exported for use anywhere in the app.
export const toast = {
  success: (title: string, description?: string) =>
    useToastStore.getState().show({ tone: 'success', title, description }),
  error: (title: string, description?: string) =>
    useToastStore.getState().show({ tone: 'error', title, description, duration: 5000 }),
  warning: (title: string, description?: string) =>
    useToastStore.getState().show({ tone: 'warning', title, description, duration: 4500 }),
  info: (title: string, description?: string) =>
    useToastStore.getState().show({ tone: 'info', title, description }),
  undo: (title: string, onUndo: () => void, duration = 5000) =>
    useToastStore.getState().show({
      tone: 'info',
      title,
      duration,
      action: { label: 'ยกเลิก', onClick: onUndo },
    }),
};

const ICONS: Record<Tone, JSX.Element> = {
  success: <CheckCircle2 className="w-5 h-5" />,
  error: <XCircle className="w-5 h-5" />,
  warning: <AlertTriangle className="w-5 h-5" />,
  info: <Info className="w-5 h-5" />,
};
const ACCENT: Record<Tone, string> = {
  success: 'border-l-emerald-500 [&>div.toast-icon]:bg-emerald-100 [&>div.toast-icon]:text-emerald-700',
  error: 'border-l-red-500 [&>div.toast-icon]:bg-red-100 [&>div.toast-icon]:text-red-700',
  warning: 'border-l-amber-500 [&>div.toast-icon]:bg-amber-100 [&>div.toast-icon]:text-amber-700',
  info: 'border-l-blue-500 [&>div.toast-icon]:bg-blue-100 [&>div.toast-icon]:text-blue-700',
};

export function Toaster() {
  const tr = useT();
  const items = useToastStore((s) => s.items);
  const dismiss = useToastStore((s) => s.dismiss);
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-20 right-5 z-[9999] flex flex-col gap-2.5 max-w-[calc(100vw-2.5rem)] w-[400px] pointer-events-none"
    >
      {items.map((t) => (
        <div
          key={t.id}
          className={cn(
            'pointer-events-auto bg-white border border-slate-200 border-l-4 rounded-xl shadow-lg',
            'flex items-start gap-3 p-3.5 animate-slide-up',
            ACCENT[t.tone]
          )}
        >
          <div className="toast-icon w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0">
            {ICONS[t.tone]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-900 leading-snug">
              {t.title}
            </div>
            {t.description && (
              <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                {t.description}
              </div>
            )}
          </div>
          {t.action && (
            <button
              type="button"
              onClick={() => {
                t.action!.onClick();
                dismiss(t.id);
              }}
              className="text-xs font-semibold text-brand-700 hover:text-brand-800 px-2.5 py-1 rounded-md hover:bg-brand-50 self-center flex-shrink-0"
            >
              {t.action.label}
            </button>
          )}
          <button
            type="button"
            onClick={() => dismiss(t.id)}
            aria-label={tr('aria.close')}
            className="text-slate-400 hover:text-slate-700 self-start p-1 rounded-md hover:bg-slate-100 flex-shrink-0"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
